import { useGlobalQuery } from 'state/query-client/hooks';
import { checkIntegrationLimit, IntegrationLimitResponse } from 'services/integration-service';
import { useConfig } from './use-config';

/**
 * Custom hook to check integration limits
 *
 * This hook retrieves the integration limit information for the current user's plan.
 * It uses the global query pattern for consistent error handling and caching.
 *
 * Features:
 * - Automatic configuration injection
 * - Global error handling with authentication management
 * - Caching and background refetching
 * - Loading and error state management
 * - TypeScript support for integration limit data
 *
 * @returns {Object} The query result object
 * @returns {IntegrationLimitResponse | undefined} data - Integration limit information
 * @returns {boolean} isLoading - Whether the query is loading
 * @returns {boolean} isError - Whether the query failed
 * @returns {boolean} isSuccess - Whether the query completed successfully
 * @returns {Error | null} error - The error if query failed
 * @returns {Function} refetch - Function to manually refetch the data
 *
 * @example
 * const { data: limitInfo, isLoading, refetch } = useCheckIntegrationLimit();
 * 
 * if (isLoading) {
 *   return <LoadingSpinner />;
 * }
 * 
 * if (limitInfo?.allowed) {
 *   return (
 *     <div>
 *       <p>Integrations available: {limitInfo.remaining} of {limitInfo.limit_value}</p>
 *       <ConnectIntegrationButton />
 *     </div>
 *   );
 * } else {
 *   return (
 *     <div>
 *       <p>Integration limit reached or not available in your plan</p>
 *       <UpgradePlanButton />
 *     </div>
 *   );
 * }
 */
export const useCheckIntegrationLimit = () => {
    const { configurationSettings } = useConfig();

    return useGlobalQuery<IntegrationLimitResponse>({
        queryKey: ['checkIntegrationLimit'],
        queryFn: async () => {
            if (!configurationSettings) {
                throw new Error('Configuration settings are not available');
            }
            return await checkIntegrationLimit(configurationSettings);
        },
        enabled: false, // Disable automatic execution
        staleTime: 5 * 60 * 1000, // 5 minutes - limit info doesn't change frequently
        refetchOnMount: false,
        refetchOnWindowFocus: false,
    });
};
